import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@arc/frontend/util-query';
import { AddArcRole, ArchiveArcRole, EditArcRole, EditArcRoleBaseMember, GetArcGroupImport, GetArcRole, GetArcRoleBaseMember, GetArcRoleImportStatus, ListArcGroups, ListArcRoles, StartArcRoleImport, UnarchiveArcRole } from '@arc/shared/util-arc-roles';
const keys = {
  all: ['arc-roles'],
  allLists: () => [...keys.all, 'list'],
  viewBaseMember: () => [...keys.allLists(), 'base'],
  viewRoleImport: () => [...keys.allLists(), 'role-import'],
  list: query => [...keys.allLists(), query],
  listGroups: () => [...keys.allLists(), 'groups'],
  viewGroupImport: () => [...keys.allLists(), 'group-import'],
  view: arcRoleId => [...keys.all, 'view', arcRoleId]
};
export const arcRolesQueryKeys = keys;
export function useQueryArcRoles(query, enabled = true) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: ({
      signal
    }) => apiRequest(ListArcRoles, {
      query
    }, {
      signal
    }),
    enabled
  });
}
export function useQueryArcGroups(enabled = true) {
  return useQuery({
    queryKey: keys.listGroups(),
    queryFn: ({
      signal
    }) => apiRequest(ListArcGroups, {}, {
      signal
    }),
    enabled
  });
}
export function useQueryArcGroupImport(enabled = true) {
  return useQuery({
    queryKey: keys.viewGroupImport(),
    queryFn: ({
      signal
    }) => apiRequest(GetArcGroupImport, {}, {
      signal
    }),
    enabled
  });
}
export function useMutationStartArcRoleImport() {
  return useMutation({
    mutationFn: async () => {
      await apiRequest(StartArcRoleImport, {});
    }
  });
}
export function useQueryArcRoleImportStatus(pollMs = 5000) {
  return useQuery({
    queryKey: keys.viewRoleImport(),
    queryFn: ({
      signal
    }) => apiRequest(GetArcRoleImportStatus, {}, {
      signal
    }),
    refetchInterval: pollMs
  });
}
export function useQueryArcRole(arcRoleId) {
  return useQuery({
    queryKey: keys.view(arcRoleId),
    queryFn: ({
      signal
    }) => apiRequest(GetArcRole, {
      params: {
        arcRoleId
      }
    }, {
      signal
    })
  });
}
async function addArcRole(dto, queryClient) {
  const data = await apiRequest(AddArcRole, {
    body: dto
  });
  void queryClient.invalidateQueries({
    queryKey: keys.allLists()
  });
  return data;
}
export function useMutationAddArcRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dto => addArcRole(dto, queryClient)
  });
}
async function editArcRole(arcRoleId, dto, queryClient) {
  const res = await apiRequest(EditArcRole, {
    params: {
      arcRoleId
    },
    body: dto
  });
  void queryClient.invalidateQueries({
    queryKey: keys.allLists()
  });
  void queryClient.invalidateQueries({
    queryKey: keys.view(arcRoleId)
  });
  return res;
}
export function useMutationEditArcRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      arcRoleId,
      dto
    }) => editArcRole(arcRoleId, dto, queryClient)
  });
}
async function archiveArcRole(arcRoleId, queryClient) {
  await apiRequest(ArchiveArcRole, {
    params: {
      arcRoleId
    }
  });
  void queryClient.invalidateQueries({
    queryKey: keys.allLists()
  });
  void queryClient.invalidateQueries({
    queryKey: keys.view(arcRoleId)
  });
}
export function useMutationArchiveArcRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: arcRoleId => archiveArcRole(arcRoleId, queryClient)
  });
}
async function unarchiveArcRole(arcRoleId, queryClient) {
  await apiRequest(UnarchiveArcRole, {
    params: {
      arcRoleId
    }
  });
  void queryClient.invalidateQueries({
    queryKey: keys.allLists()
  });
  void queryClient.invalidateQueries({
    queryKey: keys.view(arcRoleId)
  });
}
export function useMutationUnarchiveArcRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: arcRoleId => unarchiveArcRole(arcRoleId, queryClient)
  });
}
export function useQueryArcRoleBaseMember(enabled = true) {
  return useQuery({
    queryKey: keys.viewBaseMember(),
    queryFn: ({
      signal
    }) => apiRequest(GetArcRoleBaseMember, {}, {
      signal
    }),
    enabled
  });
}
async function editArcRoleBaseMember(dto, queryClient) {
  const res = await apiRequest(EditArcRoleBaseMember, {
    body: dto
  });
  void queryClient.invalidateQueries({
    queryKey: keys.allLists()
  });
  return res;
}
export function useMutationEditArcRoleBaseMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dto
    }) => editArcRoleBaseMember(dto, queryClient)
  });
}